import { useState, useEffect, useMemo, useRef, useCallback } from "react";
/* react-grid-layout v2.x dropped WidthProvider from the root export
   and moved the legacy HOC API behind /legacy. The hook-based API is
   tidier but every example in this codebase still uses the HOC form;
   /legacy keeps that working without a rewrite. */
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { loadFromSupabase } from "../supabaseDB";
import { supabase } from "../supabaseClient";
import { calcSeasonProfits, calcWeeks } from "./SeasonRegistry";
import SearchSelect from "./SearchSelect";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import { deriveDeliveryStatus, resolvePendingStage, buildSlPassingIndex } from "./saleStatusCalc";
import { buildAddressString, geocodeAddress, estimateEta } from "./etaCalc";
import { isFourSeasonsClientName } from "./emailHelpers";
import { computeCertUsage } from "./exportCertUsage";

/* ═══════════════════════════════════════════════════════════════
   Dashboard — configurable drag & drop home page

   Layout state lives in `dashboard_layouts` (one row per user_id,
   layout_json jsonb). RLS scopes every read/write to the row whose
   user_id matches auth.uid(). Falls back to localStorage when the
   user is offline so the customizations survive a refresh during a
   network outage.

   Widget registry below is the single source of truth — adding a
   new widget means: (1) drop its component, (2) add an entry to
   WIDGETS, (3) extend DEFAULT_LAYOUTS with a starting position.
   The customize modal + reset button derive from the registry, so
   no other plumbing is needed.
   ═══════════════════════════════════════════════════════════════ */

const ResponsiveGridLayout = WidthProvider(Responsive);

const T = {
  en: {
    welcome: "Business Overview",
    customize: "Customize",
    customizeTitle: "Customize Dashboard",
    customizeSub: "Toggle widgets on or off. Drag widgets in the dashboard to reorder.",
    reset: "Reset Layout",
    resetConfirm: "Restore the default dashboard layout? Your custom positions and visibility will be lost.",
    close: "Close",
    refresh: "Refresh",
    hide: "Hide widget",
    loading: "Loading…",
    /* widget titles */
    w_seasonProgress: "Season Progress",
    w_invoicesOwed: "Invoices Owed to You",
    w_billsToPay: "Bills You Need to Pay",
    w_activeShipments: "Active Shipments",
    w_organicCerts: "Export Certificates",
    w_weeklyPOs: "Weekly POs",
    /* shortcuts */
    w_products: "Products", w_clients: "Clients", w_suppliers: "Suppliers",
    w_warehouses: "Warehouses", w_settlements: "Settlements",
    w_profit: "Profit Tracker", w_payments: "Payments",
    w_certs: "Export Certificates", w_expenseBills: "Expense Bills",
    s_active: "active", s_total: "total", s_pending: "pending",
    s_last7days: "last 7 days", s_lowBal: "low balance",
    s_thisSeason: "this season",
    mainHeading: "Main widgets", shortcutsHeading: "Shortcuts",
    /* shared */
    awaiting: "Awaiting payment", overdue: "Overdue", profitGoal: "Profit Goal",
    accumulated: "Accumulated", weeklyTarget: "Weekly Target", weeklyAvg: "Weekly Avg",
    noActiveSeason: "No active season",
    daysElapsed: "Days elapsed", daysRemaining: "Days remaining", complete: "complete",
    newInvoice: "+ New Invoice", viewAll: "View all",
    addBill: "+ New Bill", payBills: "Pay bills",
    newShipment: "+ New Shipment", viewShipments: "View all",
    daysOverdue: "days overdue", dueIn: "due in", days: "days", dueToday: "due today",
    cert: "Cert #", supplier: "Supplier", used: "Used", remaining: "Remaining",
    total: "Total", expiringSoon: "Low balance — ≤3 invoices left",
    poNumber: "PO #", client: "Client", products: "Products", shipDate: "Ship",
    price: "Price", noPOsThisWeek: "No POs this week",
    fourSeasons: "Four Seasons", twinRiver: "Twin River", other: "Other",
    empty: "Nothing to show",
    pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery",
    etaLabel: "ETA ~",
  },
  es: {
    welcome: "Resumen del Negocio",
    customize: "Personalizar",
    customizeTitle: "Personalizar Dashboard",
    customizeSub: "Activa o desactiva los widgets. Arrastra para reordenar.",
    reset: "Restablecer",
    resetConfirm: "¿Restaurar el layout por defecto? Se perderá la configuración actual.",
    close: "Cerrar",
    refresh: "Actualizar",
    hide: "Ocultar widget",
    loading: "Cargando…",
    w_seasonProgress: "Avance de Temporada",
    w_invoicesOwed: "Cobros Pendientes",
    w_billsToPay: "Pagos Pendientes",
    w_activeShipments: "Embarques Activos",
    w_organicCerts: "Certificados de Exportación",
    w_weeklyPOs: "POs de la Semana",
    w_products: "Productos", w_clients: "Clientes", w_suppliers: "Proveedores",
    w_warehouses: "Bodegas", w_settlements: "Liquidaciones",
    w_profit: "Ganancias", w_payments: "Pagos",
    w_certs: "Certificados", w_expenseBills: "Facturas de Gastos",
    s_active: "activos", s_total: "total", s_pending: "pendientes",
    s_last7days: "últimos 7 días", s_lowBal: "saldo bajo",
    s_thisSeason: "esta temporada",
    mainHeading: "Widgets principales", shortcutsHeading: "Accesos rápidos",
    awaiting: "Por cobrar", overdue: "Vencido", profitGoal: "Meta",
    accumulated: "Acumulado", weeklyTarget: "Sem. Objetivo", weeklyAvg: "Sem. Promedio",
    noActiveSeason: "Sin temporada activa",
    daysElapsed: "Días transcurridos", daysRemaining: "Días restantes", complete: "completado",
    newInvoice: "+ Nueva Factura", viewAll: "Ver todo",
    addBill: "+ Nuevo Bill", payBills: "Pagar",
    newShipment: "+ Nuevo Embarque", viewShipments: "Ver todo",
    daysOverdue: "días vencido", dueIn: "vence en", days: "días", dueToday: "vence hoy",
    cert: "No. Cert", supplier: "Proveedor", used: "Usadas", remaining: "Restantes",
    total: "Total", expiringSoon: "Saldo bajo — quedan ≤3 facturas",
    poNumber: "No. PO", client: "Cliente", products: "Productos", shipDate: "Envío",
    price: "Precio", noPOsThisWeek: "Sin POs esta semana",
    fourSeasons: "Four Seasons", twinRiver: "Twin River", other: "Otros",
    empty: "Sin datos",
    pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega",
    etaLabel: "ETA ~",
  },
};

/* ───── helpers ───── */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtMoneyPrecise(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "2-digit" }); }

function calcBillSubtotal(bill) {
  var grossSales = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      grossSales += (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    }
  });
  var totalFees = 0;
  (bill.fees || []).forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var commAmt = grossSales * ((parseFloat(bill.commissionRate) || 0) / 100);
  return grossSales - commAmt - totalFees;
}
function calcInvoiceTotal(inv) {
  /* Per-line commission with invoice-level fallback + credits. Mismo
     patrón que InvoiceRegistry, InvoicePreviewModal (BillRegistry) y
     Settlements. La versión anterior ignoraba comisión, así que para
     facturas FS/TR con comisión el "due" quedaba inflado. */
  var invDefaultRate = parseFloat(inv.commissionRate) || 0;
  var gross = 0, commission = 0;
  (inv.lines || []).forEach(function (l) {
    /* Todas las líneas cuentan sin importar saleStatus. Solo el
       saldo pendiente decide si aparece en el pending list. */
    var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? invDefaultRate : (parseFloat(l.commissionRate) || 0);
    gross += amt;
    commission += amt * (rate / 100);
  });
  var tFees = 0;
  (inv.fees || []).forEach(function (fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
  var tCredits = 0;
  (inv.credits || []).forEach(function (cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
  /* Balance transfers — credit adds, debit subtracts. Mismo patrón
     que InvoiceRegistry línea 535. Sin esto, facturas liquidadas
     por transferencia de saldo aparecen como pending. */
  var tTransfers = 0;
  (inv.balanceTransfers || []).forEach(function (tr) {
    var amt = parseFloat(tr.amount) || 0;
    if (tr.type === "credit") tTransfers += amt;
    else if (tr.type === "debit") tTransfers -= amt;
  });
  return gross - commission - tFees + tCredits + tTransfers;
}

function navigate(moduleKey) { if (window.__gfiNavigate) window.__gfiNavigate(moduleKey); }
/* 2026-09-14 — Arturo: "cualquier cosa que abra desde el dashboard, al
   cerrar que me regrese al dashboard". window.__gfiReturnModule is a
   plain global (no shared store between modules, same convention as
   __gfiNavigate itself) — the target registry's own open/new hook
   captures it and, once the record it opens is closed/saved/deleted,
   navigates back here instead of leaving the user stranded on that
   module's list. Only navigateAndOpen/navigateAndCall (a Dashboard
   shortcut jumping straight to one record) set it — a plain navigate()
   to a module's list view has nothing to "return" from. */
function navigateAndCall(moduleKey, hookName) {
  window.__gfiReturnModule = "dashboard";
  if (window.__gfiNavigate) window.__gfiNavigate(moduleKey);
  if (hookName) setTimeout(function () { var fn = window[hookName]; if (typeof fn === "function") fn(); }, 200);
}
function navigateAndOpen(moduleKey, hookName, id) {
  window.__gfiReturnModule = "dashboard";
  if (window.__gfiNavigate) window.__gfiNavigate(moduleKey);
  if (hookName) setTimeout(function () { var fn = window[hookName]; if (typeof fn === "function") fn(id); }, 220);
}

function daysBetween(a, b) {
  var ms = b.getTime() - a.getTime();
  return Math.round(ms / 86400000);
}

/* ───── per-widget icons (Feather-style 16×16 strokes) ─────
   The icon sits inside the colored accent dot in the widget header.
   Each WIDGETS entry references an icon by key. */
var ICONS = {
  season: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>,
  invoiceIn: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  billOut: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>,
  truck: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2" /><path d="M16 8h4l3 3v5a1 1 0 0 1-1 1h-1" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
  leaf: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 3v9a8 8 0 1 1-8-8h9z" /><line x1="3" y1="21" x2="12" y2="12" /></svg>,
  calendar: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  /* shortcut widgets */
  products: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>,
  clients: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  suppliers: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2" /><path d="M16 8h4l3 3v5a1 1 0 0 1-1 1h-1" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
  warehouses: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
  settlements: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /><path d="M14 21h6" /></svg>,
  profit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
  payments: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M22 10H2" /><circle cx="17" cy="14" r="1.5" /></svg>,
  expenses: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="6" x2="12" y2="18" /><path d="M16 9h-6.5a2.5 2.5 0 0 0 0 5H14a2.5 2.5 0 0 1 0 5H7" /></svg>,
};

/* ───── shared card chrome ─────
   Header carries the drag handle (.dash-card-drag) + per-widget
   refresh and hide buttons. The drag handle covers the title strip
   so dragging from anywhere in that strip moves the widget. The
   refresh/hide buttons stop propagation so they don't fire a drag. */
function WidgetCard({ title, accent, icon, onClick, onRefresh, onHide, onClickAction, children }) {
  return (
    <div className="dash-card" style={accent ? { borderTop: "3px solid " + accent } : null}>
      <div className="dash-card-head dash-card-drag">
        <div className="dash-card-title" onClick={onClick} style={Object.assign({ display: "flex", alignItems: "center", gap: 8 }, onClick ? { cursor: "pointer" } : {})}>
          {icon && <span className="dash-widget-icon" style={accent ? { background: accent + "22", color: accent } : null}>{icon}</span>}
          <span>{title}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {onClickAction}
          {onRefresh && (
            <button
              className="dash-icon-btn"
              onMouseDown={function (e) { e.stopPropagation(); }}
              onClick={function (e) { e.stopPropagation(); onRefresh(); }}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          )}
          {onHide && (
            <button
              className="dash-icon-btn"
              onMouseDown={function (e) { e.stopPropagation(); }}
              onClick={function (e) { e.stopPropagation(); onHide(); }}
              title="Hide"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="dash-card-body">{children}</div>
    </div>
  );
}

function Big({ value, color, hint }) {
  /* Sizing handled by .dash-big in App.css via container-query units
     so the number scales with the card's current width. */
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <div className="dash-big" style={color ? { color: color } : null}>{value}</div>
      {hint && <div className="dash-big-hint">{hint}</div>}
    </div>
  );
}

/* ───── ShortcutCard ─────
   Compact module-link widget: big number on top, label below,
   whole body clickable. Used by the secondary widgets registered
   in WIDGETS with kind: "shortcut". `stat` is whatever the widget
   wants front-and-center (count, total $$, etc.); `substat` is an
   optional smaller line beneath (e.g. "$12,450 pending"). */
function ShortcutCard({ title, icon, accent, stat, substat, label, moduleKey, onRefresh, onHide }) {
  var go = function () { if (moduleKey) navigate(moduleKey); };
  return (
    <WidgetCard
      title={title}
      icon={icon}
      accent={accent}
      onClick={go}
      onRefresh={onRefresh}
      onHide={onHide}
    >
      <div className="dash-shortcut-body" onMouseDown={function (e) { e.stopPropagation(); }} onClick={go}>
        <div className="dash-shortcut-stat" style={accent ? { color: accent } : null}>{stat}</div>
        {substat && <div className="dash-shortcut-substat">{substat}</div>}
        {label && <div className="dash-shortcut-label">{label}</div>}
      </div>
    </WidgetCard>
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
      <div className="dash-skel" style={{ height: 16, width: "60%" }} />
      <div className="dash-skel" style={{ height: 28, width: "40%" }} />
      <div className="dash-skel" style={{ height: 12, width: "80%" }} />
      <div className="dash-skel" style={{ height: 12, width: "70%" }} />
      <div className="dash-skel" style={{ height: 12, width: "65%" }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Widget 1: Season Progress
   ═══════════════════════════════════════════════════════════════ */
function SeasonProgressCard({ t, lang, refreshKey, onRefresh, onHide }) {
  var data = useMemo(function () {
    var seasons = loadLS("gfi-seasons", []);
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var active = seasons.find(function (s) { return s.status === "open"; });
    if (!active) {
      active = seasons.find(function (s) {
        if (!s.startDate || !s.endEstimate) return false;
        return new Date(s.startDate + "T00:00:00") <= today && today <= new Date(s.endEstimate + "T00:00:00");
      });
    }
    if (!active) return null;

    var profits = calcSeasonProfits();
    var sp = profits[active.id] || { total: 0, weeklyReal: 0 };
    var totalProfit = sp.total || 0;
    var weeklyReal = sp.weeklyReal || 0;

    var totalWeeks = calcWeeks(active.startDate, active.endEstimate);
    var goal = parseFloat(active.profitGoal) || 0;
    var weeklyTarget = totalWeeks > 0 ? goal / totalWeeks : 0;

    /* Time-based progress (per the spec — días transcurridos / restantes / %) */
    var start = active.startDate ? new Date(active.startDate + "T00:00:00") : null;
    var end   = active.endEstimate ? new Date(active.endEstimate + "T00:00:00") : null;
    var totalDays = (start && end) ? Math.max(1, daysBetween(start, end)) : 0;
    var elapsedDays = (start) ? Math.max(0, daysBetween(start, today)) : 0;
    var remainingDays = Math.max(0, totalDays - elapsedDays);
    var pct = totalDays > 0 ? Math.min(100, Math.round((elapsedDays / totalDays) * 100)) : 0;

    var seasonName = active.seasonName || (active.startDate || "").substring(0, 4) + "–" + (active.endEstimate || "").substring(2, 4);

    return {
      seasonName: seasonName, goal: goal, accumulated: totalProfit,
      weeklyTarget: weeklyTarget, weeklyActual: weeklyReal,
      totalDays: totalDays, elapsedDays: elapsedDays, remainingDays: remainingDays, pct: pct,
      startDate: active.startDate, endDate: active.endEstimate,
    };
  }, [refreshKey]);

  return (
    <WidgetCard
      title={t.w_seasonProgress}
      onClick={function () { navigate("seasons"); }}
      accent="#5BBCB0"
      icon={ICONS.season}
      onRefresh={onRefresh}
      onHide={onHide}
      onClickAction={data ? <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontWeight: 600, marginRight: 4 }}>{data.seasonName}</span> : null}
    >
      {!data ? (
        <div className="dash-empty">{t.noActiveSeason}</div>
      ) : (
        <>
          {/* Time progress — primary visual per spec */}
          <div style={{ height: 12, background: "var(--color-bg)", borderRadius: 6, overflow: "hidden", border: "1px solid var(--color-border)", marginTop: 4 }}>
            <div style={{ width: data.pct + "%", height: "100%", background: data.pct >= 85 ? "#DC2626" : data.pct >= 60 ? "#F59E0B" : "#5BBCB0", transition: "width 0.4s" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: "var(--color-text-muted)" }}>
            <span>{fmtDate(data.startDate)}</span>
            <span style={{ fontWeight: 700, color: "var(--color-text-secondary)" }}>{data.pct}% {t.complete}</span>
            <span>{fmtDate(data.endDate)}</span>
          </div>

          <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.daysElapsed}</div>
              <Big value={data.elapsedDays} color="#1a2636" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.daysRemaining}</div>
              <Big value={data.remainingDays} color={data.remainingDays < 30 ? "#DC2626" : "#1a2636"} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 18, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--color-border)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.profitGoal}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700 }}>{fmtMoney(data.goal)}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.accumulated}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: data.accumulated >= 0 ? "#059669" : "#DC2626" }}>{fmtMoney(data.accumulated)}</div>
            </div>
          </div>
        </>
      )}
    </WidgetCard>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Widget 2: Invoices Owed to You
   ═══════════════════════════════════════════════════════════════ */
/* EXCEPTION to the season-filter rule (see seasonFilter.js / CLAUDE.md
   REGLA DE FILTRO DE SEASON): InvoicesOwedCard and BillsToPayCard do
   NOT take a `filterSeasons` prop and never scope by season. Arturo's
   own call — an unpaid balance from a closed season is still real,
   active debt and must never be hidden by a default Open-seasons
   filter. Do not wire the Dashboard season filter into these two. */
function InvoicesOwedCard({ t, lang, refreshKey, onRefresh, onHide }) {
  var data = useMemo(function () {
    var invoices = loadLS("gfi-invoices", []);
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var list = [];
    var totalDue = 0;
    invoices.forEach(function (inv) {
      /* Ningún filtro por invoice.status — solo importa el saldo. */
      var total = calcInvoiceTotal(inv);
      var paidSum = (inv.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var paid = paidSum > 0 ? paidSum : (parseFloat(inv.amountPaid) || 0);
      var due = +(total - paid).toFixed(2);
      if (due <= 0.01) return;
      totalDue += due;
      var daysOver = 0;
      if (inv.dueDate) {
        var dd = new Date(inv.dueDate + "T00:00:00");
        daysOver = daysBetween(dd, today);
      }
      list.push({
        id: inv.id, puNumber: inv.puNumber, clientName: inv.clientName,
        amount: due, dueDate: inv.dueDate, daysOver: daysOver,
      });
    });
    list.sort(function (a, b) { return b.daysOver - a.daysOver; });
    return { totalDue: totalDue, count: list.length, list: list };
  }, [refreshKey]);

  return (
    <WidgetCard
      title={t.w_invoicesOwed}
      onClick={function () { navigate("invoices"); }}
      accent="#c65d00"
      icon={ICONS.invoiceIn}
      onRefresh={onRefresh}
      onHide={onHide}
    >
      <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.awaiting}</div>
          <Big value={fmtMoneyPrecise(data.totalDue)} color="#c65d00" hint={data.count + " " + (lang === "es" ? "facturas" : "invoices")} />
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
          <button className="dash-btn dash-btn-primary" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function (e) { e.stopPropagation(); navigateAndCall("invoices", "__gfiInvoiceNew"); }}>{t.newInvoice}</button>
        </div>
      </div>
      {data.list.length === 0 ? (
        <div className="dash-empty">{t.empty}</div>
      ) : (
        <div className="dash-scroll" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {data.list.map(function (r) {
            var isOver = r.daysOver > 0;
            return (
              <div key={r.id} className="dash-list-row" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function () { navigateAndOpen("invoices", "__gfiInvoiceOpen", r.id); }}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "#c65d00" }}>{r.puNumber || r.id}</span>
                    {isOver && <span style={{ padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: "rgba(220,38,38,0.10)", color: "#DC2626" }}>{r.daysOver} {t.daysOverdue}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.clientName || "—"}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: isOver ? "#DC2626" : "#c65d00" }}>{fmtMoneyPrecise(r.amount)}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{fmtDate(r.dueDate)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Widget 3: Bills You Need to Pay
   ═══════════════════════════════════════════════════════════════ */
function BillsToPayCard({ t, lang, refreshKey, onRefresh, onHide }) {
  var data = useMemo(function () {
    var bills = loadLS("gfi-bills", []);
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var list = [];
    var totalDue = 0;
    bills.forEach(function (b) {
      if (b.status === "draft" || b.status === "paid") return;
      var sub = calcBillSubtotal(b);
      var paidSum = (b.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var paid = paidSum > 0 ? paidSum : (parseFloat(b.amountPaid) || 0);
      var due = +(sub - paid).toFixed(2);
      if (due <= 0.01) return;
      totalDue += due;
      var daysToDue = 0;
      if (b.dueDate) {
        var dd = new Date(b.dueDate + "T00:00:00");
        daysToDue = daysBetween(today, dd);
      }
      list.push({
        id: b.id, lot: b.lot, shipperName: b.shipperName,
        amount: due, dueDate: b.dueDate, daysToDue: daysToDue,
      });
    });
    list.sort(function (a, b) { return a.daysToDue - b.daysToDue; });
    return { totalDue: totalDue, count: list.length, list: list };
  }, [refreshKey]);

  return (
    <WidgetCard
      title={t.w_billsToPay}
      onClick={function () { navigate("bills"); }}
      accent="#2A7A6F"
      icon={ICONS.billOut}
      onRefresh={onRefresh}
      onHide={onHide}
    >
      <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.awaiting}</div>
          <Big value={fmtMoneyPrecise(data.totalDue)} color="#2A7A6F" hint={data.count + " bills"} />
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
          <button className="dash-btn dash-btn-primary" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function (e) { e.stopPropagation(); navigateAndCall("bills", "__gfiBillNew"); }}>{t.addBill}</button>
        </div>
      </div>
      {data.list.length === 0 ? (
        <div className="dash-empty">{t.empty}</div>
      ) : (
        <div className="dash-scroll" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {data.list.map(function (r) {
            var isOver = r.daysToDue < 0;
            var isToday = r.daysToDue === 0;
            var chipBg = isOver ? "rgba(220,38,38,0.10)" : (isToday ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.10)");
            var chipFg = isOver ? "#DC2626" : (isToday ? "#B45309" : "#059669");
            var chipText = isOver ? Math.abs(r.daysToDue) + " " + t.daysOverdue : (isToday ? t.dueToday : t.dueIn + " " + r.daysToDue + " " + t.days);
            return (
              <div key={r.id} className="dash-list-row" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function () { navigateAndOpen("bills", "__gfiBillOpen", r.id); }}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "#2A7A6F" }}>LOT {r.lot || r.id}</span>
                    <span style={{ padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: chipBg, color: chipFg }}>{chipText}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.shipperName || "—"}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#2A7A6F" }}>{fmtMoneyPrecise(r.amount)}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{fmtDate(r.dueDate)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}

/* Numeric PU# sort key — strips any non-digit prefix/suffix so "PU#
   17445"/"17445" both sort as 17445. Missing/non-numeric PU#s sort to
   the end (Infinity) instead of collapsing to 0 and jumping first. */
function puNumSort(puNumber) {
  var n = parseInt(String(puNumber || "").replace(/[^0-9]/g, ""), 10);
  return isNaN(n) ? Infinity : n;
}

/* ═══════════════════════════════════════════════════════════════
   Widget 4: Active Shipments (latest 10)
   ═══════════════════════════════════════════════════════════════ */
function ActiveShipmentsCard({ t, lang, refreshKey, onRefresh, onHide, filterSeasons }) {
  /* "Active" here means the SALE side (client delivery confirmation),
     not the inbound shipment-cost lifecycle (draft/in_transit/billed
     used to gate this widget) — Arturo: a shipment stops being
     active once the client has reported a real delivery status
     (Good Delivery/Protection/PAS/etc.); until then it's always
     either Pending Pick Up or Pending Delivery. Same
     resolvePendingStage default used across Invoice/Bill/Shipment/SL,
     see saleStatusCalc.js. */
  var [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function (sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);

  var items = useMemo(function () {
    var ships = loadLS("gfi-shipments", []);
    var seasons = loadLS("gfi-seasons", []);
    var invoices = loadLS("gfi-invoices", []);
    var clients = loadLS("gfi-clients", []);
    var warehouses = loadLS("gfi-warehouses", []);

    /* 2026-09-12 — Arturo: a lot can carry several PUs (POs) at once,
       each one confirmed independently by the client — showing a
       single rolled-up badge per lot hid that (e.g. one PU already
       Good Delivery, another still Pending Pick Up). Per-PU status
       now, same deriveDeliveryStatus/resolvePendingStage every other
       screen uses (never a frozen value — computed live from each
       invoice's own lines). A lot only appears here at all once it
       has at least one PU that's STILL pending; a fully-delivered
       shipment (every PU resolved to a real status) disappears
       entirely, per Arturo: "cuando el embarque ya esta entregado ya
       no aparece nada". */
    var list = ships.map(function (s) {
      var wh = warehouses.find(function (w) { return w && (String(w.id) === String(s.warehouseId) || String(w._uuid) === String(s.warehouseId)); });
      var originAddress = buildAddressString(wh);
      var lotInvoices = invoices.filter(function (inv) { return String(inv.lot) === String(s.lot); });
      var activePUs = lotInvoices.map(function (inv) {
        var status = deriveDeliveryStatus((inv.lines || []).map(function (l) { return { saleStatus: l.saleStatus }; })) || resolvePendingStage([inv], slPassingIndex);
        /* Client short name per PU (2026-09-12, Arturo: "el PU no
           muestra el nombre corto del cliente, solo muestra el
           nombre corto del shipper en el lot number") — the lot row
           already shows the SHIPPER (who grew/shipped the fruit in);
           each PU line needs the CLIENT (who's buying that specific
           PO out), since a lot can be split across different clients. */
        var cli = clients.find(function (c) { return c && (String(c.id) === String(inv.clientId) || String(c._uuid) === String(inv.clientId)); });
        var clientShort = (cli && (cli.shortName || cli.fullName)) || inv.clientName || "";
        /* ETA inputs (2026-09-12, Arturo) — pickupDate/pickupTime are
           entered by hand on the Invoice from the driver's BOL; the
           actual geocode + HOS estimate runs async in a separate
           effect below (network call), this just carries the address
           strings/timestamp it needs. */
        var destAddress = buildAddressString(cli);
        /* Per-PU cases/pallets (2026-09-12, Arturo: "veo 58048 y 630 al
           final, pero las PO no muestran cantidades, cada una debiera
           mostrar cuanto lleva en cajas y pallets") — the shipment-level
           total on the right was already there, but it rolls up every
           PU together, hiding how the load is actually split across
           them. */
        var puCases = (inv.lines || []).reduce(function (sum, l) { return sum + (parseInt(l.cases, 10) || 0); }, 0);
        var puPallets = (inv.lines || []).reduce(function (sum, l) { return sum + (parseFloat(l.pallets) || 0); }, 0);
        /* 2026-09-14 — Arturo: the Four Seasons truck isn't a direct
           run, it makes stops for other deliveries along the way — the
           ETA below needs +8h padding for FS destinations specifically
           (see etaCalc.js's MULTI_STOP_EXTRA_HOURS). Checked against
           both the resolved client and the invoice's own free-text
           name, same fallback the rest of the app uses when a client
           isn't in the catalog. */
        var isFourSeasons = isFourSeasonsClientName(clientShort) || isFourSeasonsClientName(inv.clientName);
        return { id: inv.id, puNumber: inv.puNumber, status: status, clientShort: clientShort, pickupDate: inv.pickupDate || "", pickupTime: inv.pickupTime || "", originAddress: originAddress, destAddress: destAddress, cases: puCases, pallets: puPallets, isFourSeasons: isFourSeasons };
      }).filter(function (p) { return p.status === "pendingPickUp" || p.status === "pendingDelivery"; });
      /* 2026-09-30, Arturo: "ordenalos por numerico ascendente tomando
         como referencia el numero de pu" — PUs within a lot now sort
         ascending by their own numeric PU#, not insertion order. */
      activePUs.sort(function (a, b) { return puNumSort(a.puNumber) - puNumSort(b.puNumber); });
      return Object.assign({}, s, { __activePUs: activePUs });
    }).filter(function (s) { return s.__activePUs.length > 0; });

    if (filterSeasons && filterSeasons.length > 0) {
      list = list.filter(function (s) { return filterSeasons.indexOf(resolveSeasonId(seasons, s.dateArrival || s.dateDeparture || "", s.seasonId)) >= 0; });
    }
    /* Lots themselves sort ascending by their lowest active PU# — same
       reference Arturo asked for, applied one level up so a lot with
       PU# 17440 sorts before one starting at 17445 regardless of
       arrival date. */
    return list
      .sort(function (a, b) {
        var aMin = Math.min.apply(null, a.__activePUs.map(function (p) { return puNumSort(p.puNumber); }));
        var bMin = Math.min.apply(null, b.__activePUs.map(function (p) { return puNumSort(p.puNumber); }));
        return aMin - bMin;
      })
      .slice(0, 10);
  }, [refreshKey, filterSeasons, slPassingIndex]);

  /* ETA per PU (2026-09-12, Arturo: "si nexus puede calcular... asi en
     la tarjeta de embarques si hay embarques pending delivery, puede
     decir ETA fecha y hora aprox") — runs separately from `items`
     because geocoding is a network call (etaCalc.js's geocodeAddress,
     cached in localStorage so a repeat address never re-hits the
     network). Only PUs with a recorded pickupDate get an ETA; the
     rest just show their status badge with no ETA line, same as
     today. Best-effort: a geocode miss or network hiccup just leaves
     that PU without an ETA line, never breaks the card. */
  var [etaByPu, setEtaByPu] = useState({});
  useEffect(function () {
    var cancelled = false;
    (async function () {
      var results = {};
      for (var i = 0; i < items.length; i++) {
        var puList = items[i].__activePUs || [];
        for (var j = 0; j < puList.length; j++) {
          var p = puList[j];
          if (!p.pickupDate || !p.originAddress || !p.destAddress) continue;
          var pickupDateObj = new Date(p.pickupDate + "T" + (p.pickupTime || "00:00") + ":00");
          if (isNaN(pickupDateObj.getTime())) continue;
          var origin = await geocodeAddress(p.originAddress);
          if (cancelled) return;
          var dest = await geocodeAddress(p.destAddress);
          if (cancelled) return;
          if (!origin || !dest) continue;
          var est = estimateEta(pickupDateObj, origin, dest, { multiStop: p.isFourSeasons });
          if (est) results[p.id] = est.eta;
        }
      }
      if (!cancelled) setEtaByPu(results);
    })();
    return function () { cancelled = true; };
  }, [items]);

  var STATUS_COLORS = {
    pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B" },
    pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB" },
  };

  return (
    <WidgetCard
      title={t.w_activeShipments}
      onClick={function () { navigate("shipments"); }}
      accent="#7C3AED"
      icon={ICONS.truck}
      onRefresh={onRefresh}
      onHide={onHide}
      onClickAction={<button className="dash-btn" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function (e) { e.stopPropagation(); navigateAndCall("shipments", "__gfiShipmentNew"); }}>{t.newShipment}</button>}
    >
      {items.length === 0 ? (
        <div className="dash-empty">{t.empty}</div>
      ) : (
        <div className="dash-scroll" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {items.map(function (s) {
            var cases = (s.lines || []).reduce(function (sum, l) { return sum + (parseInt(l.cases, 10) || 0); }, 0);
            /* Shipment-level pallets total (2026-09-12, Arturo: "asi como
               el embarque agregar pallets tambien") — cases-only total
               was already shown; pallets sits right next to it. */
            var pallets = (s.lines || []).reduce(function (sum, l) { return sum + (parseFloat(l.pallets) || 0); }, 0);
            return (
              <div key={s.id} className="dash-list-row" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function () { navigateAndOpen("shipments", "__gfiShipmentOpen", s.id); }}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "#7C3AED" }}>LOT {s.lot}</span>
                  <div style={{ fontSize: 12, color: "var(--color-text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.shipperName || "—"}</div>
                  {/* One line per still-active PU (2026-09-12, Arturo) —
                      a lot can have several PUs each confirmed
                      independently; a single rolled-up badge hid that. */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 3 }}>
                    {s.__activePUs.map(function (p) {
                      var puSc = STATUS_COLORS[p.status] || STATUS_COLORS.pendingPickUp;
                      var eta = etaByPu[p.id];
                      return (
                        <div key={p.id} style={{ display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {/* 2026-09-14, Arturo: clicking the PU# must open
                                the linked INVOICE, not the shipment — pickup
                                date/time (and the ETA above) live on the
                                Invoice, not on the Shipment record. Its own
                                click target so it doesn't also fire the row's
                                shipment-open handler. */}
                            <span
                              onMouseDown={function (e) { e.stopPropagation(); }}
                              onClick={function (e) { e.stopPropagation(); navigateAndOpen("invoices", "__gfiInvoiceOpen", p.id); }}
                              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2 }}
                            >PU# {p.puNumber || "—"}</span>
                            {p.clientShort && <span style={{ fontSize: 10, color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.clientShort}</span>}
                            {/* Per-PU cases/pallets (2026-09-12, Arturo:
                                "las PO no muestran cantidades, cada una
                                debiera mostrar cuanto lleva en cajas y
                                pallets") — the shipment-level total on
                                the right rolls every PU together; this
                                is how much THIS specific PU carries. */}
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: "var(--color-text-muted)", flexShrink: 0 }}>{p.cases}c{p.pallets > 0 ? " · " + (Number.isInteger(p.pallets) ? p.pallets : p.pallets.toFixed(1)) + "p" : ""}</span>
                            <span style={{ padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: puSc.bg, color: puSc.text, flexShrink: 0 }}>{t[p.status] || p.status}</span>
                          </div>
                          {/* Approximate ETA (2026-09-12, Arturo) — straight-line
                              distance + simplified HOS drive-time model from the
                              recorded pickup timestamp, see etaCalc.js. Only shows
                              once a pickup date/time has been entered on the
                              Invoice AND both addresses geocoded successfully. */}
                          {eta && (
                            <span style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 1 }}>
                              {t.etaLabel} {eta.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "short", day: "2-digit" })} {eta.toLocaleTimeString(lang === "es" ? "es-MX" : "en-US", { hour: "numeric", minute: "2-digit" })}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700 }}>{cases}c{pallets > 0 ? " · " + (Number.isInteger(pallets) ? pallets : pallets.toFixed(1)) + "p" : ""}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{fmtDate(s.dateArrival || s.dateDeparture)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Widget 5: Organic Certificates
   ═══════════════════════════════════════════════════════════════ */
function OrganicCertsCard({ t, lang, refreshKey, onRefresh, onHide, filterSeasons }) {
  var items = useMemo(function () {
    var certs = loadLS("gfi-export-certs", []);
    var suppliers = loadLS("gfi-suppliers", []);
    var seasons = loadLS("gfi-seasons", []);
    /* 2026-09-14 — Arturo: cert 840-1560017750-260001 showed 11 used
       when only 7 shipper invoices actually used it. Root cause: this
       card counted DISTINCT CLIENT invoices (gfi-invoices — the sale
       side, Ground-Fresh → client) referencing the cert, one per
       line.exportCertId — but a single shipper invoice/lot often gets
       split across 2+ client PU invoices (Twin River + Four Seasons on
       the same lot), so each split PU counted as its own "use" even
       though it's the SAME real shipper invoice. An export certificate
       is consumed on the SHIPPER side (one use per unique shipper
       invoice, see exportCertUsage.js's documented rule), not the
       client side — same bug this card had, the real Export
       Certificates module never had it (see ExportCertificateRegistry.js's
       resolvedCerts, which already used computeCertUsage(c, shipments,
       bills)). Switched to the same shared helper so this card and the
       module always agree. */
    var shipments = loadLS("gfi-shipments", []);
    var bills = loadLS("gfi-bills", []);
    var usageByCert = {};
    certs.forEach(function (c) { usageByCert[c.id] = computeCertUsage(c, shipments, bills).length; });
    var todayISO = new Date().toISOString().substring(0, 10);
    return certs
      .filter(function (c) {
        var total = parseInt(c.totalInvoices, 10) || 0;
        var used = usageByCert[c.id] || 0;
        var pTo = c.periodTo || c.period_to;
        if (pTo && String(pTo) < todayISO) return false; /* expirado */
        if (filterSeasons && filterSeasons.length > 0 && filterSeasons.indexOf(resolveSeasonId(seasons, c.periodFrom, c.seasonId)) < 0) return false;
        return total > 0 && used < total;
      })
      .map(function (c) {
        var total = parseInt(c.totalInvoices, 10) || 0;
        var used = usageByCert[c.id] || 0;
        var remaining = total - used;
        var sup = suppliers.find(function (s) { return String(s.id) === String(c.supplierId); });
        var supName = sup ? (sup.shortName || sup.fullName || sup.name) : (c.shipperName || "—");
        return {
          id: c.id, certNumber: c.certNumber, supplierName: supName,
          total: total, used: used, remaining: remaining, low: remaining <= 3,
        };
      })
      .sort(function (a, b) { return a.remaining - b.remaining; })
      .slice(0, 8);
  }, [refreshKey, filterSeasons]);

  return (
    <WidgetCard
      title={t.w_organicCerts}
      onClick={function () { navigate("export_certs"); }}
      accent="#059669"
      icon={ICONS.leaf}
      onRefresh={onRefresh}
      onHide={onHide}
    >
      {items.length === 0 ? (
        <div className="dash-empty">{t.empty}</div>
      ) : (
        <div className="dash-scroll" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {items.map(function (r) {
            return (
              <div key={r.id} className="dash-list-row" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function () { navigate("export_certs"); }} style={{ background: r.low ? "rgba(220,38,38,0.04)" : undefined }}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "#059669" }}>{r.certNumber || "—"}</span>
                    {r.low && <span style={{ padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: "rgba(220,38,38,0.10)", color: "#DC2626" }} title={t.expiringSoon}>⚠ {r.remaining} {t.remaining.toLowerCase()}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.supplierName}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: r.low ? "#DC2626" : "#059669" }}>{r.used}/{r.total}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{r.remaining} {t.remaining.toLowerCase()}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Widget 6: Weekly POs — grouped by client
   ═══════════════════════════════════════════════════════════════ */
function WeeklyPOsCard({ t, lang, refreshKey, onRefresh, onHide }) {
  var groups = useMemo(function () {
    var fixedPos = loadLS("gfi-fixed-pos", []);
    var consignmentPos = loadLS("gfi-consignment-pos", []);
    var all = (fixedPos || []).concat(consignmentPos || []);
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var day = today.getDay() || 7; /* Mon=1..Sun=7 */
    var weekStart = new Date(today); weekStart.setDate(today.getDate() - (day - 1));
    var weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23, 59, 59);

    var inRange = function (d) {
      if (!d) return false;
      var dt = new Date(d + "T00:00:00");
      return dt >= weekStart && dt <= weekEnd;
    };

    /* Only POs still waiting on a PU — Arturo: this card is meant to
       flag what's coming up and still needs a PU# assigned, not to
       re-list POs already turned into a shipment/invoice. Checked
       against real production data before shipping this: `puNumber`
       (not `linkedInvoiceId`) is the reliable signal — syncPoLinkage
       in InvoiceRegistry.js writes both together going forward, but
       almost all existing PO rows (29/35 in a live spot-check) only
       ever got `puNumber` set, through an older/bulk path that never
       touched linkedInvoiceId. Using linkedInvoiceId alone would have
       shown nearly every already-invoiced PO as "still needs a PU". */
    var filtered = all.filter(function (p) {
      return (inRange(p.deliveryDate) || inRange(p.date)) && !p.puNumber;
    });

    /* Group by clientName, treating FS / TR as their own buckets so the
       split mirrors how Arturo runs the week.  */
    var groupOrder = [];
    var byGroup = {};
    var pushGroup = function (key, label) {
      if (!byGroup[key]) { byGroup[key] = { key: key, label: label, rows: [] }; groupOrder.push(key); }
    };
    filtered.forEach(function (p) {
      var name = (p.clientName || "").toLowerCase();
      var key, label;
      if (name.indexOf("four seasons") >= 0) { key = "fs"; label = t.fourSeasons; }
      else if (name.indexOf("twin river") >= 0) { key = "tr"; label = t.twinRiver; }
      else { key = "other"; label = p.clientName || t.other; }
      pushGroup(key, label);
      byGroup[key].rows.push(p);
    });

    /* Inside each group, sort by ship/delivery date ascending. */
    groupOrder.forEach(function (k) {
      byGroup[k].rows.sort(function (a, b) {
        var ad = a.deliveryDate || a.date || "";
        var bd = b.deliveryDate || b.date || "";
        return ad.localeCompare(bd);
      });
    });

    return groupOrder.map(function (k) { return byGroup[k]; });
  }, [refreshKey, t]);

  var totalCount = groups.reduce(function (s, g) { return s + g.rows.length; }, 0);

  return (
    <WidgetCard
      title={t.w_weeklyPOs}
      onClick={function () { navigate("fixed_pricing"); }}
      accent="#2563EB"
      icon={ICONS.calendar}
      onRefresh={onRefresh}
      onHide={onHide}
      onClickAction={<span style={{ fontSize: 11, color: "var(--color-text-muted)", fontWeight: 600, marginRight: 4 }}>{totalCount} POs</span>}
    >
      {totalCount === 0 ? (
        <div className="dash-empty">{t.noPOsThisWeek}</div>
      ) : (
        <div className="dash-scroll" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {groups.map(function (g) {
            return (
              <div key={g.key}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)", marginBottom: 4 }}>{g.label} ({g.rows.length})</div>
                {g.rows.slice(0, 5).map(function (p) {
                  var price = parseFloat(p.priceFS || p.priceSL || p.priceGF || p.salePrice || 0);
                  return (
                    <div key={p.id} className="dash-list-row" onMouseDown={function (e) { e.stopPropagation(); }} onClick={function () { navigate("fixed_pricing"); }}>
                      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "#2563EB" }}>{p.poNumber || "—"}</span>
                          {p.saleStatus && <span style={{ padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 600, background: "rgba(37,99,235,0.10)", color: "#2563EB" }}>{p.saleStatus}</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.productName || p.productDescription || "—"}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700 }}>{price > 0 ? fmtMoneyPrecise(price) : "—"}</div>
                        <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{fmtDate(p.deliveryDate || p.date)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Shortcut widgets — compact module-link cards
   Each reads from its own legacy gfi-* cache and surfaces a single
   stat. Off by default; user enables via the Customize modal.
   ═══════════════════════════════════════════════════════════════ */
function ProductsShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  var count = useMemo(function () {
    return loadLS("gfi-products", []).filter(function (p) { return p.isActive !== false; }).length;
  }, [refreshKey]);
  return <ShortcutCard title={t.w_products} icon={ICONS.products} accent="#7C3AED" stat={count.toLocaleString()} label={t.s_active} moduleKey="products" onRefresh={onRefresh} onHide={onHide} />;
}
function ClientsShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  var count = useMemo(function () { return loadLS("gfi-clients", []).length; }, [refreshKey]);
  return <ShortcutCard title={t.w_clients} icon={ICONS.clients} accent="#2563EB" stat={count.toLocaleString()} label={t.s_total} moduleKey="clients" onRefresh={onRefresh} onHide={onHide} />;
}
function SuppliersShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  var count = useMemo(function () {
    return loadLS("gfi-suppliers", []).filter(function (s) { return s.isActive !== false; }).length;
  }, [refreshKey]);
  return <ShortcutCard title={t.w_suppliers} icon={ICONS.suppliers} accent="#0891B2" stat={count.toLocaleString()} label={t.s_active} moduleKey="suppliers" onRefresh={onRefresh} onHide={onHide} />;
}
function WarehousesShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  var count = useMemo(function () { return loadLS("gfi-warehouses", []).length; }, [refreshKey]);
  return <ShortcutCard title={t.w_warehouses} icon={ICONS.warehouses} accent="#0E7490" stat={count.toLocaleString()} label={t.s_total} moduleKey="warehouses" onRefresh={onRefresh} onHide={onHide} />;
}
function SettlementsShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  /* "Pending settlement" = a bill with shipper-side balance still
     open. Mirrors the same filter BillsToPayCard uses. */
  var data = useMemo(function () {
    var bills = loadLS("gfi-bills", []);
    var count = 0; var total = 0;
    bills.forEach(function (b) {
      if (b.status === "draft" || b.status === "paid") return;
      var sub = calcBillSubtotal(b);
      var paidSum = (b.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var paid = paidSum > 0 ? paidSum : (parseFloat(b.amountPaid) || 0);
      var due = +(sub - paid).toFixed(2);
      if (due > 0.01) { count++; total += due; }
    });
    return { count: count, total: total };
  }, [refreshKey]);
  return <ShortcutCard title={t.w_settlements} icon={ICONS.settlements} accent="#B45309" stat={data.count.toLocaleString()} substat={fmtMoney(data.total)} label={t.s_pending} moduleKey="settlements" onRefresh={onRefresh} onHide={onHide} />;
}
function ProfitShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  /* Total profit for the active season — reuses calcSeasonProfits. */
  var data = useMemo(function () {
    var seasons = loadLS("gfi-seasons", []);
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var active = seasons.find(function (s) { return s.status === "open"; });
    if (!active) {
      active = seasons.find(function (s) {
        if (!s.startDate || !s.endEstimate) return false;
        return new Date(s.startDate + "T00:00:00") <= today && today <= new Date(s.endEstimate + "T00:00:00");
      });
    }
    if (!active) return { total: 0, hasSeason: false };
    var profits = calcSeasonProfits();
    var sp = profits[active.id] || { total: 0 };
    return { total: sp.total || 0, hasSeason: true };
  }, [refreshKey]);
  return <ShortcutCard title={t.w_profit} icon={ICONS.profit} accent={data.total >= 0 ? "#059669" : "#DC2626"} stat={data.hasSeason ? fmtMoney(data.total) : "—"} label={t.s_thisSeason} moduleKey="profit_tracking" onRefresh={onRefresh} onHide={onHide} />;
}
function PaymentsShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  /* Count + total of payments in the last 7 days. */
  var data = useMemo(function () {
    var pays = loadLS("gfi-payments", []);
    var cutoff = new Date(); cutoff.setHours(0, 0, 0, 0); cutoff.setDate(cutoff.getDate() - 7);
    var count = 0; var total = 0;
    pays.forEach(function (p) {
      var d = p.paymentDate ? new Date(p.paymentDate + "T00:00:00") : null;
      if (!d || d < cutoff) return;
      count++;
      total += (p.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
    });
    return { count: count, total: total };
  }, [refreshKey]);
  return <ShortcutCard title={t.w_payments} icon={ICONS.payments} accent="#1B3A4B" stat={data.count.toLocaleString()} substat={fmtMoney(data.total)} label={t.s_last7days} moduleKey="payments" onRefresh={onRefresh} onHide={onHide} />;
}
function CertsShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  /* Active cert count + how many have ≤3 invoices left. Live count
     desde invoices en vez del cache stale del cert. */
  var data = useMemo(function () {
    var certs = loadLS("gfi-export-certs", []);
    var invoices = loadLS("gfi-invoices", []);
    var usageByCert = {};
    invoices.forEach(function (inv) {
      var used = new Set();
      (inv.lines || []).forEach(function (l) {
        if (l.exportCertId) used.add(l.exportCertId);
      });
      used.forEach(function (cid) {
        usageByCert[cid] = (usageByCert[cid] || 0) + 1;
      });
    });
    var todayISO = new Date().toISOString().substring(0, 10);
    var active = 0; var low = 0;
    certs.forEach(function (c) {
      var total = parseInt(c.totalInvoices, 10) || 0;
      var used = usageByCert[c.id] || 0;
      var pTo = c.periodTo || c.period_to;
      if (pTo && String(pTo) < todayISO) return; /* expirado */
      if (total <= 0 || used >= total) return;
      active++;
      if ((total - used) <= 3) low++;
    });
    return { active: active, low: low };
  }, [refreshKey]);
  var accent = data.low > 0 ? "#DC2626" : "#059669";
  return <ShortcutCard title={t.w_certs} icon={ICONS.leaf} accent={accent} stat={data.active.toLocaleString()} substat={data.low > 0 ? data.low + " " + t.s_lowBal : null} label={t.s_active} moduleKey="export_certs" onRefresh={onRefresh} onHide={onHide} />;
}
function ExpenseBillsShortcut({ t, lang, refreshKey, onRefresh, onHide }) {
  /* Unpaid expense bills + total still due. */
  var data = useMemo(function () {
    var bills = loadLS("gfi-expense-bills", []);
    var count = 0; var total = 0;
    bills.forEach(function (b) {
      if (b.status === "paid" || b.status === "draft") return;
      var amt = parseFloat(b.totalAmount || b.amount || 0) || 0;
      var paid = (b.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var due = +(amt - paid).toFixed(2);
      if (due > 0.01) { count++; total += due; }
    });
    return { count: count, total: total };
  }, [refreshKey]);
  return <ShortcutCard title={t.w_expenseBills} icon={ICONS.expenses} accent="#9333EA" stat={data.count.toLocaleString()} substat={fmtMoney(data.total)} label={t.s_pending} moduleKey="expense_bills" onRefresh={onRefresh} onHide={onHide} />;
}

/* ═══════════════════════════════════════════════════════════════
   Widget registry + default layout
   ═══════════════════════════════════════════════════════════════ */
const WIDGETS = [
  /* Primary widgets — visible by default. */
  { id: "season_progress",  tKey: "w_seasonProgress",  C: SeasonProgressCard,  primary: true },
  { id: "invoices_owed",    tKey: "w_invoicesOwed",    C: InvoicesOwedCard,    primary: true },
  { id: "bills_to_pay",     tKey: "w_billsToPay",      C: BillsToPayCard,      primary: true },
  { id: "active_shipments", tKey: "w_activeShipments", C: ActiveShipmentsCard, primary: true },
  { id: "organic_certs",    tKey: "w_organicCerts",    C: OrganicCertsCard,    primary: true },
  { id: "weekly_pos",       tKey: "w_weeklyPOs",       C: WeeklyPOsCard,       primary: true },
  /* Shortcut widgets — off by default; the user toggles them on in
     the Customize modal. */
  { id: "products_short",     tKey: "w_products",     C: ProductsShortcut },
  { id: "clients_short",      tKey: "w_clients",      C: ClientsShortcut },
  { id: "suppliers_short",    tKey: "w_suppliers",    C: SuppliersShortcut },
  { id: "warehouses_short",   tKey: "w_warehouses",   C: WarehousesShortcut },
  { id: "settlements_short",  tKey: "w_settlements",  C: SettlementsShortcut },
  { id: "profit_short",       tKey: "w_profit",       C: ProfitShortcut },
  { id: "payments_short",     tKey: "w_payments",     C: PaymentsShortcut },
  { id: "certs_short",        tKey: "w_certs",        C: CertsShortcut },
  { id: "expense_bills_short",tKey: "w_expenseBills", C: ExpenseBillsShortcut },
];

const DEFAULT_VISIBLE = WIDGETS.filter(function (w) { return w.primary; }).map(function (w) { return w.id; });

/* Layout per breakpoint. h is rowHeight units (rowHeight=56).
   12-col grid on lg, 8 on md, 4 on sm. Primary widgets keep their
   spacious defaults; shortcut widgets default to a compact 3×3
   tile and are appended at the bottom so they don't displace the
   primaries when the user enables them. minW=1, minH=2 lets the
   user shrink anything down to a tile. */
const DEFAULT_LAYOUTS = {
  lg: [
    { i: "season_progress",  x: 0, y: 0,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "invoices_owed",    x: 4, y: 0,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "bills_to_pay",     x: 8, y: 0,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "active_shipments", x: 0, y: 6,  w: 6, h: 7, minW: 1, minH: 2 },
    { i: "organic_certs",    x: 6, y: 6,  w: 6, h: 7, minW: 1, minH: 2 },
    { i: "weekly_pos",       x: 0, y: 13, w: 12, h: 7, minW: 1, minH: 2 },
    { i: "products_short",     x: 0,  y: 20, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "clients_short",      x: 3,  y: 20, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "suppliers_short",    x: 6,  y: 20, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "warehouses_short",   x: 9,  y: 20, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "settlements_short",  x: 0,  y: 23, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "profit_short",       x: 3,  y: 23, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "payments_short",     x: 6,  y: 23, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "certs_short",        x: 9,  y: 23, w: 3, h: 3, minW: 1, minH: 2 },
    { i: "expense_bills_short",x: 0,  y: 26, w: 3, h: 3, minW: 1, minH: 2 },
  ],
  md: [
    { i: "season_progress",  x: 0, y: 0,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "invoices_owed",    x: 4, y: 0,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "bills_to_pay",     x: 0, y: 6,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "active_shipments", x: 4, y: 6,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "organic_certs",    x: 0, y: 12, w: 4, h: 6, minW: 1, minH: 2 },
    { i: "weekly_pos",       x: 4, y: 12, w: 4, h: 6, minW: 1, minH: 2 },
    { i: "products_short",     x: 0, y: 18, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "clients_short",      x: 2, y: 18, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "suppliers_short",    x: 4, y: 18, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "warehouses_short",   x: 6, y: 18, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "settlements_short",  x: 0, y: 21, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "profit_short",       x: 2, y: 21, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "payments_short",     x: 4, y: 21, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "certs_short",        x: 6, y: 21, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "expense_bills_short",x: 0, y: 24, w: 2, h: 3, minW: 1, minH: 2 },
  ],
  sm: [
    { i: "season_progress",  x: 0, y: 0,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "invoices_owed",    x: 0, y: 6,  w: 4, h: 6, minW: 1, minH: 2 },
    { i: "bills_to_pay",     x: 0, y: 12, w: 4, h: 6, minW: 1, minH: 2 },
    { i: "active_shipments", x: 0, y: 18, w: 4, h: 7, minW: 1, minH: 2 },
    { i: "organic_certs",    x: 0, y: 25, w: 4, h: 7, minW: 1, minH: 2 },
    { i: "weekly_pos",       x: 0, y: 32, w: 4, h: 7, minW: 1, minH: 2 },
    { i: "products_short",     x: 0, y: 39, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "clients_short",      x: 2, y: 39, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "suppliers_short",    x: 0, y: 42, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "warehouses_short",   x: 2, y: 42, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "settlements_short",  x: 0, y: 45, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "profit_short",       x: 2, y: 45, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "payments_short",     x: 0, y: 48, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "certs_short",        x: 2, y: 48, w: 2, h: 3, minW: 1, minH: 2 },
    { i: "expense_bills_short",x: 0, y: 51, w: 2, h: 3, minW: 1, minH: 2 },
  ],
};

const DEFAULT_LAYOUT = { visible: DEFAULT_VISIBLE, layouts: DEFAULT_LAYOUTS };
const LS_KEY = "gfi-dashboard-layout";

/* ═══════════════════════════════════════════════════════════════
   Customize modal
   ═══════════════════════════════════════════════════════════════ */
function CustomizeModal({ t, visible, onToggle, onReset, onClose }) {
  var primaries = WIDGETS.filter(function (w) { return w.primary; });
  var shortcuts = WIDGETS.filter(function (w) { return !w.primary; });
  var renderRow = function (w) {
    var checked = visible.indexOf(w.id) >= 0;
    return (
      <label key={w.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, background: checked ? "rgba(42,122,111,0.06)" : "var(--color-bg)", border: "1px solid " + (checked ? "rgba(42,122,111,0.2)" : "var(--color-border)"), cursor: "pointer" }}>
        <input type="checkbox" checked={checked} onChange={function () { onToggle(w.id); }} style={{ accentColor: "var(--color-accent)" }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)" }}>{t[w.tKey]}</span>
      </label>
    );
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={function (e) { e.stopPropagation(); }} style={{ background: "var(--color-bg-card)", borderRadius: 12, padding: 24, width: 520, maxWidth: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--color-text)" }}>{t.customizeTitle}</h3>
          <button onClick={onClose} className="dash-icon-btn" title={t.close}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 16 }}>{t.customizeSub}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18, overflowY: "auto", flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginTop: 2 }}>{t.mainHeading}</div>
          {primaries.map(renderRow)}
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginTop: 10 }}>{t.shortcutsHeading}</div>
          {shortcuts.map(renderRow)}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <button className="dash-btn" onClick={onReset}>{t.reset}</button>
          <button className="dash-btn dash-btn-primary" onClick={onClose}>{t.close}</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Persistence helpers
   ═══════════════════════════════════════════════════════════════ */
async function fetchUserLayout() {
  try {
    var s = await supabase.auth.getSession();
    var uid = s && s.data && s.data.session && s.data.session.user && s.data.session.user.id;
    if (!uid) return null;
    var res = await supabase.from("dashboard_layouts").select("layout_json").eq("user_id", uid).maybeSingle();
    if (res.error) return null;
    return res.data ? res.data.layout_json : null;
  } catch (e) { return null; }
}

async function saveUserLayout(layout) {
  try {
    var s = await supabase.auth.getSession();
    var uid = s && s.data && s.data.session && s.data.session.user && s.data.session.user.id;
    if (!uid) return;
    await supabase.from("dashboard_layouts").upsert(
      { user_id: uid, layout_json: layout, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    );
  } catch (e) { /* swallow — localStorage mirror still holds the layout */ }
}

/* Sanity-check a layout-shape blob loaded from Supabase / LS. Reject
   anything missing visible+layouts so a corrupt row doesn't break the
   render — the user just falls back to defaults. */
function normalizeLayout(raw) {
  if (!raw || typeof raw !== "object") return null;
  var vis = Array.isArray(raw.visible) ? raw.visible.filter(function (v) { return WIDGETS.some(function (w) { return w.id === v; }); }) : null;
  var layouts = raw.layouts && typeof raw.layouts === "object" ? raw.layouts : null;
  if (!vis || !layouts || !layouts.lg) return null;
  return { visible: vis, layouts: layouts };
}

/* ═══════════════════════════════════════════════════════════════
   MAIN — Dashboard
   ═══════════════════════════════════════════════════════════════ */
export default function Dashboard({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang];

  var [visible, setVisible] = useState(DEFAULT_LAYOUT.visible);
  var [layouts, setLayouts] = useState(DEFAULT_LAYOUT.layouts);
  var [refreshKey, setRefreshKey] = useState(0);
  var [showCustomize, setShowCustomize] = useState(false);
  var [loaded, setLoaded] = useState(false);
  /* Not persisted across sessions, per REGLA DE FILTRO DE SEASON — each
     mount starts back on Open seasons only. Only ActiveShipmentsCard and
     OrganicCertsCard consume this; InvoicesOwedCard/BillsToPayCard are a
     deliberate exception (see comment above InvoicesOwedCard). */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var skipNextSaveRef = useRef(true);  /* don't save the initial seed */

  /* Refetches every source table from Supabase into its gfi-* localStorage
     mirror, then bumps refreshKey so every widget's useMemo recomputes
     against the fresh data. Kept at component scope (not nested inside
     the mount effect) so both the mount bootstrap AND the "Refresh"
     button/per-card refresh icons can call the same real refetch.

     2026-09-14 — Arturo: "el refresh no funciona en ninguna tarjeta".
     Root cause: refreshAll only called setRefreshKey directly, which
     forces a re-render but recomputes every card's useMemo against the
     SAME stale gfi-* localStorage snapshot — nothing actually changes on
     screen unless something else (window focus, tab visibility change)
     had already silently refetched in the background. The button looked
     like it did something (matches window.__gfiDashboardRefresh, which
     DOES refetch, already used by other modules to nudge the Dashboard)
     but never pulled anything new. Now both the button and every card's
     own refresh icon go through this real refetch. */
  var aliveRef = useRef(true);
  var preload = useCallback(function () {
    var pairs = [
      ["invoices",      "gfi-invoices"],
      ["bills",         "gfi-bills"],
      ["payments",      "gfi-payments"],
      ["seasons",       "gfi-seasons"],
      ["shipments",     "gfi-shipments"],
      ["expense_bills", "gfi-expense-bills"],
      ["suppliers",     "gfi-suppliers"],
      ["clients",       "gfi-clients"],
      ["export_certs",  "gfi-export-certs"],
      ["fixed_pos",     "gfi-fixed-pos"],
      ["consignment_pos","gfi-consignment-pos"],
    ];
    return Promise.all(pairs.map(function (p) {
      return loadFromSupabase(p[0], { force: true }).then(function (rows) {
        try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
      }).catch(function () {});
    })).then(function () { if (aliveRef.current) setRefreshKey(function (k) { return k + 1; }); });
  }, []);

  /* Mount: hydrate from Supabase (fallback to LS) and seed the
     legacy `gfi-*` caches so the widget useMemos have fresh data. */
  useEffect(function () {
    aliveRef.current = true;
    var bootstrap = function () {
      fetchUserLayout().then(function (remote) {
        if (!aliveRef.current) return;
        var raw = normalizeLayout(remote);
        if (!raw) {
          try { raw = normalizeLayout(JSON.parse(localStorage.getItem(LS_KEY) || "null")); } catch (e) {}
        }
        if (raw) {
          setVisible(raw.visible);
          setLayouts(raw.layouts);
        }
        setLoaded(true);
        /* Keep the seed save guard armed for one render so the initial
           setVisible/setLayouts above doesn't trigger a write. */
        setTimeout(function () { skipNextSaveRef.current = false; }, 50);
      });
    };

    bootstrap();
    preload();
    var bump = function () { preload(); };
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", bump);
    window.__gfiDashboardRefresh = bump;
    return function () {
      aliveRef.current = false;
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", bump);
      delete window.__gfiDashboardRefresh;
    };
  }, [preload]);

  /* Debounced persist on layouts/visible change. We dedupe writes
     with a 600ms timer so a drag-resize that fires onLayoutChange
     20 times only hits Supabase once.

     2026-09-14 — Arturo: resizing a card and then immediately leaving
     the Dashboard (switching modules — App.js unmounts this component
     on every module switch) used to lose the resize entirely: the
     per-change cleanup below only CANCELS the pending 600ms timer, it
     never flushes it, so a resize made within that window never got
     saved and the next visit reloaded the old layout ("si reduzco una
     tarjeta al salir de esa seccion y regresar todo se restaura").
     `pendingRef` always holds the latest not-yet-persisted payload (or
     null once flushed); a dedicated unmount-only effect below flushes
     it immediately when the component goes away, instead of letting it
     be silently discarded. */
  var saveTimerRef = useRef(null);
  var pendingRef = useRef(null);
  var flushSave = useCallback(function () {
    if (!pendingRef.current) return;
    var payload = pendingRef.current;
    pendingRef.current = null;
    try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch (e) {}
    saveUserLayout(payload);
  }, []);
  useEffect(function () {
    if (!loaded || skipNextSaveRef.current) return;
    pendingRef.current = { visible: visible, layouts: layouts };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, 600);
    return function () { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [visible, layouts, loaded, flushSave]);
  useEffect(function () {
    return function () { flushSave(); };
  }, [flushSave]);

  var handleLayoutChange = useCallback(function (_currentLayout, allLayouts) {
    setLayouts(allLayouts);
  }, []);

  var hideWidget = useCallback(function (id) {
    setVisible(function (prev) { return prev.filter(function (x) { return x !== id; }); });
  }, []);

  var toggleWidget = useCallback(function (id) {
    setVisible(function (prev) {
      if (prev.indexOf(id) >= 0) return prev.filter(function (x) { return x !== id; });
      return prev.concat([id]);
    });
  }, []);

  var resetLayout = useCallback(function () {
    if (!window.confirm(t.resetConfirm)) return;
    setVisible(DEFAULT_LAYOUT.visible);
    setLayouts(DEFAULT_LAYOUT.layouts);
  }, [t]);

  var refreshAll = useCallback(function () { preload(); }, [preload]);

  /* Build the children list. Each widget needs `key` to match its
     layout entry. We pass refreshKey through so the widget's useMemo
     recomputes when the global refresh bumps. */
  var children = visible.map(function (id) {
    var w = WIDGETS.find(function (x) { return x.id === id; });
    if (!w) return null;
    var C = w.C;
    return (
      <div key={id}>
        <C
          t={t}
          lang={lang}
          refreshKey={refreshKey}
          onRefresh={refreshAll}
          onHide={function () { hideWidget(id); }}
          filterSeasons={filterSeasons}
        />
      </div>
    );
  }).filter(Boolean);

  if (!loaded) {
    return (
      <div className="dash-root">
        <h2 className="dash-title">{t.welcome}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {[0, 1, 2, 3, 4, 5].map(function (i) {
            return (
              <div key={i} className="dash-card" style={{ minHeight: 240 }}>
                <div className="dash-skel" style={{ height: 16, width: "55%", marginBottom: 14 }} />
                <LoadingSkeleton />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="dash-root">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <h2 className="dash-title" style={{ margin: 0 }}>{t.welcome}</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} style={{ minWidth: 180 }} groups={buildSeasonFilterOptions(lang)} />
          <button className="dash-btn" onClick={refreshAll} title={t.refresh}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", marginRight: 4 }}>
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {t.refresh}
          </button>
          <button className="dash-btn dash-btn-primary" onClick={function () { setShowCustomize(true); }}>⚙ {t.customize}</button>
        </div>
      </div>
      <ResponsiveGridLayout
        className="dash-rgl"
        layouts={layouts}
        breakpoints={{ lg: 1100, md: 760, sm: 0 }}
        cols={{ lg: 12, md: 8, sm: 4 }}
        rowHeight={56}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        draggableHandle=".dash-card-drag"
        onLayoutChange={handleLayoutChange}
        compactType="vertical"
        isResizable={true}
        isDraggable={true}
      >
        {children}
      </ResponsiveGridLayout>
      {showCustomize && (
        <CustomizeModal
          t={t}
          visible={visible}
          onToggle={toggleWidget}
          onReset={function () { resetLayout(); setShowCustomize(false); }}
          onClose={function () { setShowCustomize(false); }}
        />
      )}
    </div>
  );
}
